import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuotes } from "../../../quotes/useQuotes";
import { useCompany } from "../../../../context/CompanyContext";
import { Button } from "@/components/ui/button";
import { Plus, FileText } from "lucide-react";

/**
 * Convert slug back to display name
 */
const tagToDisplayName = (tag) => {
  if (!tag) return '';
  return tag
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Get a consistent color for a service tag
 */
const getTagColor = (tag) => {
  const colors = [
    { bg: 'bg-blue-100', text: 'text-blue-700' },
    { bg: 'bg-green-100', text: 'text-green-700' },
    { bg: 'bg-purple-100', text: 'text-purple-700' },
    { bg: 'bg-orange-100', text: 'text-orange-700' },
    { bg: 'bg-pink-100', text: 'text-pink-700' },
    { bg: 'bg-cyan-100', text: 'text-cyan-700' },
  ];
  let hash = 0;
  for (let i = 0; i < tag.length; i++) {
    hash = tag.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

/**
 * QuotesTab
 *
 * Displays quotes associated with a client or lead.
 * Queries companies/{companyId}/quotes where clientId or leadId matches.
 * 
 * Detection logic:
 * - If client.clientId exists → it's a converted client, use clientId
 * - If client.leadId exists → it's a converted client, fall back to leadId
 * - Otherwise → it's a lead, use client.id as leadId
 */
const QuotesTab = ({ client }) => {
  const { companyId } = useCompany();
  const navigate = useNavigate();

  // Determine if this is a client or a lead
  // Clients have a clientId field, leads don't
  const isClient = Boolean(client?.clientId);
  const isConvertedClient = Boolean(client?.leadId);
  
  // For clients: use clientId, fall back to leadId for converted clients
  // For leads: use the id as leadId
  const clientId = isClient ? client.clientId : null;
  const leadId = isConvertedClient ? client.leadId : (!isClient ? client?.id : null);

  // Fetch quotes with correct entity type
  const { quotes, loading } = useQuotes({
    clientId,
    leadId,
    companyId,
  });

  // Loading state
  if (loading) {
    return (
      <div className="text-sm text-gray-500 p-4">
        Loading quotes…
      </div>
    );
  }

  // No identity - graceful empty state
  if (!clientId && !leadId) {
    return (
      <div className="text-sm text-gray-500 p-4">
        No client selected.
      </div>
    );
  }

  return (
    <div className="space-y-4">

      <div className="flex justify-between items-center mb-3">
        <h2 className="text-lg font-semibold">Quotes</h2>

        {(clientId || leadId) && (
          <Button
            variant="default"
            size="sm"
            onClick={() => {
              const param = clientId ? `clientId=${clientId}` : `leadId=${leadId}`;
              navigate(`/quotes/new?${param}`);
            }}
            className="flex items-center gap-1"
            data-testid="new-quote-btn"
          >
            <Plus className="w-4 h-4" />
            New Quote
          </Button>
        )}
      </div>

      <div className="space-y-3">
        {quotes.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">No quotes yet</p>
            <p className="text-xs text-gray-400 mt-1">
              Create a quote to get started
            </p>
          </div>
        ) : (
          quotes.map((quote) => {
            const quoteId = quote.quoteId;
            const tags = quote.tags || [];

            return (
              <div
                key={quoteId}
                className="p-4 border rounded-md cursor-pointer hover:bg-gray-50 transition"
                onClick={() => navigate(`/quotes/${quoteId}?companyId=${companyId}`)}
                data-testid={`quote-item-${quoteId}`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      Quote #{quoteId?.slice(-6) || quoteId}
                    </div>
                    <div className="text-xs text-gray-500">
                      {quote.createdAt
                        ? new Date(quote.createdAt).toLocaleString()
                        : "No timestamp"}
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-sm font-semibold text-gray-800">
                      ${quote?.totals?.grandTotal?.toFixed(2) || "0.00"}
                    </div>
                    <div className="text-xs text-gray-500 capitalize">
                      {quote.status || "draft"}
                    </div>
                  </div>
                </div>

                {/* Service badges */}
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {tags.slice(0, 3).map((tag) => {
                      const color = getTagColor(tag);
                      return (
                        <span
                          key={tag}
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${color.bg} ${color.text}`}
                        >
                          {tagToDisplayName(tag)}
                        </span>
                      );
                    })}
                    {tags.length > 3 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                        +{tags.length - 3}
                      </span>
                    )}
                  </div>
                )}

              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default QuotesTab;

